import { API } from "@editorjs/editorjs"

/**
 * The save data for the Markdown tool.
 */
export interface MarkdownData {
    text?: string;
}

/**
 * The configuration options for the markdown tool.
 */
export interface MarkdownConfig {
    /** The binary file type unique identifier to use when uploading. */
    binaryFileType?: string;

    /** The hidden field selector that will be used to store any uploaded file identifiers. */
    uploadedFilesSelector?: string;
}

/**
 * The structure of the data returned by the upload handler.
 */
interface RockUploadedFile {
    /** The binary file identifier for the uploaded file. */
    Id: number;

    /** The name of the uploaded file. */
    FileName: string;
}

/**
 * A block tool that allows the user to enter markdown text in the same format
 * as supported by the Rockumentation system.
 */
export class Markdown {
    /** The element that wraps all our user interface content. */
    private wrapper!: HTMLDivElement;

    /** The text area that is used by the ACE editor. */
    private editorTextArea!: HTMLTextAreaElement;

    /** The binary file type unique identifier to use when uploading a file. */
    private binaryFileType?: string;

    /** The DOM selector to use when storing the uploaded file identifier. */
    private uploadedFilesSelector?: string;

    /**
     * Creates a new instance of the Markdown block tool.
     * 
     * @param config The initialization parameters for the tool.
     */
    constructor(config: { data: MarkdownData, config: MarkdownConfig, api: API, readOnly: boolean }) {
        /* Default to the Unsecured file type if it wasn't specified. */
        this.binaryFileType = config.config.binaryFileType;
        this.uploadedFilesSelector = config.config.uploadedFilesSelector;

        this.setupContent(config.data);
    }

    /**
     * Determines if line breaks should be supported in the tool.
     * 
     * @returns true if line breaks (and the enter key) are allowed; otherwise false.
     */
    static get enableLineBreaks() {
        return true;
    }

    /**
     * Get the details that describe the button in the toolbox to switch
     * this block to Markdown editor.
     * 
     * @returns The button details.
     */
    static get toolbox() {
        return {
            title: 'Markdown',
            icon: '<svg xmlns="http://www.w3.org/2000/svg" width="208" height="128" viewBox="0 0 208 128"><path d="M30 98V30h20l20 25 20-25h20v68H90V59L70 84 50 59v39zm125 0l-30-33h20V30h20v35h20z"/></svg>'
        };
    }

    /**
     * The function that is called once a file has been uploaded.
     * 
     * @param uploadedFile The information returned by the file uploader.
     * @returns The content to be inserted into the markdown text.
     */
    private uploadImageHelper(uploadedFile: RockUploadedFile) {
        var imageChar = '';
        if ((/\.(gif|jpg|jpeg|tif|tiff|png)$/i).test(uploadedFile.FileName)) {
            imageChar = '!';
        }

        if (this.uploadedFilesSelector !== undefined) {
            const hfUploadedFiles = <HTMLInputElement>document.querySelector(this.uploadedFilesSelector);
            if (hfUploadedFiles !== null) {
                hfUploadedFiles.value = `${hfUploadedFiles.value},${uploadedFile.Id}`;
            }
        }

        let pathRoot = '/';

        return imageChar + '[' + uploadedFile.FileName + '](' + pathRoot + 'GetFile.ashx?Id=' + uploadedFile.Id + ')';
    }

    /**
     * Called when the user wishes to preview what they have written.
     * 
     * @param content The markdown content to be converted to HTML.
     * @param callback The callback to call when the conversion has finished.
     */
    async previewContent(content: string, callback: Function) {
        let result = await $.ajax({
            url: '/api/BBM_Rockumentation_Utility/PreviewMarkdown',
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify(content)
        });

        callback(result);
    }

    /**
     * Prepares the DOM editor that will be used to display our custom UI.
     * 
     * @param data The initial content data.
     */
    setupContent(data: MarkdownData) {
        this.wrapper = document.createElement("div");
        this.editorTextArea = document.createElement("textarea");
        this.wrapper.appendChild(this.editorTextArea);

        if (data && data.text) {
            this.editorTextArea.value = data.text;
        }

        const $domEditor = $(this.editorTextArea);
        (<any>$domEditor).markdownEditor({
            height: "auto",
            fullscreen: false,
            preview: true,
            onPreview: this.previewContent,
            imageUpload: this.binaryFileType !== undefined && this.binaryFileType !== "",
            uploadPath: `/FileUploader.ashx?isBinaryFile=1&fileTypeGuid=${this.binaryFileType}`,
            uploadHelper: this.uploadImageHelper.bind(this)
        });

        var editor = (<any>$domEditor).markdownEditor('getEditor');

        editor.setOptions({ minLines: 8, maxLines: Infinity });
        editor.container.style.height = (editor.getSession().getScreenLength() * editor.renderer.lineHeight + editor.renderer.scrollBar.getWidth()) + 'px';
        editor.resize();

        $domEditor.on('focus', () => editor.focus());
    }

    /**
     * Renders the DOM element that will display our UI.
     * 
     * @returns The DOM element that will display our UI.
     */
    public render() {
        return this.wrapper;
    }

    /**
     * Save this block's content and return the data that can be used to
     * re-create it later.
     * 
     * @param blockContent The DOM element that contains our UI.
     * @returns The saved data for this block.
     */
    public save(blockContent: any) {
        let data : MarkdownData = {
            text: this.editorTextArea.value
        };

        return data;
    }

    /**
     * Checks if this block's content should be included in the editor's save data.
     * 
     * @param savedData The data generated by our save method.
     * @returns true if this block should be saved; otherwise false.
     */
    public validate(savedData: MarkdownData) {
        return savedData.text !== "";
    }
}
